import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../elements/ActionElement';

const allowedParts = ['tab', 'panel'];

const validatePartName = function (partName) {
  if (!allowedParts.includes(partName)) {
    throw new Error(`Invalid part name "${partName}" - expected ${allowedParts.join(', ')}`);
  }
};

const mergeAttributes = function (
  inputAttributes,
  attributesNormal,
  classes,
  index,
  visiblePanelIndex,
  attributesActive
) {
  let attributes = {
    ...attributesNormal,
    ...inputAttributes
  };

  const { className } = attributes;

  if (className) {
    classes.push(className);
  }

  if (index === visiblePanelIndex) {
    classes.push('active');
    if (attributesActive) {
      attributes = {
        ...attributes,
        ...attributesActive
      };
    }
  }
  else {
    const activeClassIndex = classes.indexOf('active');
    if (activeClassIndex > -1) {
      classes.splice(activeClassIndex, 1);
    }
  }
  if (classes.length > 0) {
    attributes.className = [...new Set(classes)].join(' ');
  }
  return attributes;
};

class Tabs extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      visiblePanelIndex: props.firstVisibleTabIndex || 0,
      instanceUuid: uuidv4()
    };

    this.getPartId = this.getPartId.bind(this);
    this.getTabElement = this.getTabElement.bind(this);
    this.getPanelElement = this.getPanelElement.bind(this);
    this.getParts = this.getParts.bind(this);
    this.validateTabData = this.validateTabData.bind(this);
    this.showPanel = this.showPanel.bind(this);
  }

  getPartId(partName, index) {
    validatePartName(partName);

    const {
      state: {
        instanceUuid
      }
    } = this;

    return `${partName}-${instanceUuid}-${index}`;
  }

  getTabElement({ classes = [], content, attributes }, index, showPanel, visiblePanelIndex) {
    const { getPartId } = this;
    const tabId = getPartId('tab', index);
    const panelId = getPartId('panel', index);
    const attributesNormal = {
      id: tabId,
      key: tabId,
      className: 'tab',
      role: 'tab',
      buttonColor: 'secondary',
      sameTextAndIconColor: false,
      disabled: false,
      onClick: showPanel.bind(this, index),
      'aria-selected': false,
      'aria-controls': panelId,
      type: 'button'
    };
    const attributesActive = { 'aria-selected': true };
    const finalAttributes = mergeAttributes(
      attributes,
      attributesNormal,
      classes,
      index,
      visiblePanelIndex,
      attributesActive
    );

    return <Button {...finalAttributes}>{content}</Button>;
  }

  getPanelElement(elementData, index, visiblePanelIndex) {
    const {
      classes = [],
      attributes,
      content,
      wrapperTagName: WrapperTagName = 'section'
    } = elementData;

    const { getPartId } = this;
    const tabId = getPartId('tab', index);
    const panelId = getPartId('panel', index);
    const attributesNormal = {
      id: panelId,
      key: panelId,
      className: 'panel',
      role: 'tabpanel',
      'aria-labelledby': tabId,
      'aria-controls': panelId
    };

    const finalAttributes = mergeAttributes(attributes, attributesNormal, classes, index, visiblePanelIndex);
    return <WrapperTagName {...finalAttributes}>{content}</WrapperTagName>;
  }

  getParts(partName) {
    validatePartName(partName);

    const {
      props: {
        children
      },
      state: {
        visiblePanelIndex
      },
      showPanel,
      getTabElement,
      getPanelElement
    } = this;

    console.log('children', children);
    console.log('object inner', children.map(({ [partName]: elementData }, index) => (partName === 'tab'
      ? getTabElement(elementData, index, showPanel, visiblePanelIndex)
      : getPanelElement(elementData, index, visiblePanelIndex))
    ));

    return children.map(({ [partName]: elementData }, index) => (partName === 'tab'
      ? getTabElement(elementData, index, showPanel, visiblePanelIndex)
      : getPanelElement(elementData, index, visiblePanelIndex))
    );
  }

  validateTabData() {
    const {
      props: {
        children
      }
    } = this;

    const requiredProps = ['tab', 'panel'];

    if (!Array.isArray(children)) {
      throw new Error(
        'ERROR: Invalid data type was passed to Tabs component - '
        + `expected an array but got: "${children}"`
      );
    }

    children.forEach(tabDataItem => {
      requiredProps.forEach(requiredProp => {
        if (!tabDataItem[requiredProp]) {
          const errorId = uuid.v1();
          console.error(
            'Error ID:', `${errorId}`,
            'Invalid tab data item:', tabDataItem,
            'Missing required prop:', `"${requiredProp}"`
          );

          throw new Error(
            'ERROR: Invalid data structure was passed to Tabs component - '
            + `expected an array of objects with props "${requiredProps.join('", "')}", `
            + `but array contains an invalid item (see error logs above with error ID "${errorId}")`
          );
        }
      });
    });
  }

  showPanel(visiblePanelIndex) {
    this.setState({ visiblePanelIndex });
  }

  render() {
    const {
      props: {
        tablistTitle,
        tablistClasses = [],
        wrapperTagName: WrapperTagName = 'article'
      },
      getParts,
      validateTabData
    } = this;

    validateTabData();

    return (
      <WrapperTagName className='tabs'>
        <header>
          <nav className={[...tablistClasses, 'tablist'].join(' ')} role='tablist' title={tablistTitle}>
            {getParts('tab')}
          </nav>
        </header>

        {getParts('panel')}
      </WrapperTagName>
    );
  }
}

Tabs.propTypes = {
  tablistTitle: PropTypes.string.isRequired,
  wrapperTagName: PropTypes.string,
  children: PropTypes.arrayOf(
    PropTypes.shape({
      tab: PropTypes.shape({
        content: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.element
        ]).isRequired,
        classes: PropTypes.arrayOf(PropTypes.string),
        attributes: PropTypes.object
      }).isRequired,
      panel: PropTypes.shape({
        content: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.element
        ]).isRequired,
        classes: PropTypes.arrayOf(PropTypes.string),
        attributes: PropTypes.object,
        wrapperTagName: PropTypes.string
      }).isRequired
    })
  ),
  tablistClasses: PropTypes.arrayOf(PropTypes.string),
  firstVisibleTabIndex: PropTypes.number
};


export default Tabs;